// src/modules/appointments/components/AppointmentForm.jsx
import { useState, useEffect } from "react";
import ContactSelect from "./ContactSelect";

const initialState = {
  start_at: "",
  end_at: "",
  timezone: "America/Guayaquil",
  location_id: "",
  resource_id: "",
  subject_contact_id: "",
  service_id: "",
  reason_id: "",
  status: "pending_approval",
  notes: "",
  total_amount: "",
  requires_approval: true,
};

function todayStartInput(timeZone) {
  if (timeZone && timeZone !== "local") {
    try {
      const parts = new Intl.DateTimeFormat("en", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(new Date());
      const get = (type) => parts.find((part) => part.type === type)?.value;
      return `${get("year")}-${get("month")}-${get("day")}T00:00`;
    } catch {
      // Si la zona no es válida, usamos la fecha local del navegador.
    }
  }
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
}

function isBeforeToday(value, timeZone) {
  return !!value && value.slice(0, 10) < todayStartInput(timeZone).slice(0, 10);
}

function servicePrice(service) {
  const value = service?.price ?? service?.base_price;
  return value === "" || value == null ? null : Number(value);
}

function serviceOptionLabel(service) {
  const price = servicePrice(service);
  const duration = service?.default_duration_min
    ? ` - ${service.default_duration_min} min`
    : "";
  const amount = Number.isFinite(price) ? ` - $${price.toFixed(2)}` : "";
  return `${service.name}${duration}${amount}`;
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const formatDate = (d) => {
    if (!d || Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatDate(value);
  }
  if (typeof value === "string") {
    if (value.includes("T")) return value.replace(" ", "T").slice(0, 16);
    const d = new Date(value.replace(" ", "T"));
    return formatDate(d);
  }
  const d = new Date(value);
  return formatDate(d);
}

export default function AppointmentForm({ initialData, config, onSubmit, submitting }) {
  const [form, setForm] = useState(initialState);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (initialData) {
      setForm({
        ...initialState,
        ...initialData,
        resource_id: initialData.resources?.[0]?.resource_id || initialData.resource_id || "",
        start_at: toDateTimeLocal(initialData.start_at),
        end_at: toDateTimeLocal(initialData.end_at),
      });
    }
  }, [initialData]);

  const services = Array.isArray(config?.services) ? config.services : [];
  const locations = Array.isArray(config?.locations) ? config.locations : [];
  const resources = Array.isArray(config?.resources) ? config.resources : [];
  const reasons = Array.isArray(config?.reasons) ? config.reasons : [];
  const selectedLocation = locations.find((l) => Number(l.id) === Number(form.location_id));
  const effectiveTimezone =
    selectedLocation?.timezone ||
    form.timezone ||
    config?.settings?.timezone ||
    "America/Guayaquil";

  function applyServiceDefaults(next, serviceId) {
    const service = services.find((s) => Number(s.id) === Number(serviceId));
    if (!service) {
      next.total_amount = "";
      return next;
    }

    const duration = Number(service.default_duration_min || 0);
    if (next.start_at && duration > 0) {
      const start = new Date(next.start_at);
      if (!Number.isNaN(start.getTime())) {
        const end = new Date(start.getTime() + duration * 60000);
        next.end_at = toDateTimeLocal(end);
      }
    }

    const price = servicePrice(service);
    if (Number.isFinite(price)) {
      next.total_amount = String(price);
    }

    if (service.requires_approval != null) {
      next.requires_approval = !!service.requires_approval;
    }

    return next;
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setValidationError("");
    setForm((f) => {
      let next = {
        ...f,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "service_id") next = applyServiceDefaults(next, value);
      if (name === "start_at" && next.service_id) {
        next = applyServiceDefaults(next, next.service_id);
      }
      return next;
    });
  }

  function setField(name, value) {
    setValidationError("");
    setForm((f) => ({
      ...f,
      [name]: value,
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const isCreate = !initialData?.id;
    if (isCreate && isBeforeToday(form.start_at, effectiveTimezone)) {
      setValidationError("No se puede crear una cita en una fecha anterior a hoy.");
      return;
    }
    if (form.start_at && form.end_at && form.end_at <= form.start_at) {
      setValidationError("La fecha/hora de fin debe ser posterior al inicio.");
      return;
    }
    const resourceId = form.resource_id || null;
    onSubmit &&
      onSubmit({
        ...form,
        location_id: form.location_id || null,
        resource_id: resourceId,
        subject_contact_id: form.subject_contact_id || null,
        service_id: form.service_id || null,
        reason_id: form.reason_id || null,
        timezone:
          effectiveTimezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          "America/Guayaquil",
        total_amount: form.total_amount
          ? Number(form.total_amount)
          : undefined,
      });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {validationError && (
        <div className="appointment-field-error border border-red-400/40 rounded p-2">
          {validationError}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ContactSelect
          value={form.subject_contact_id || ""}
          onChange={(value) => setField("subject_contact_id", value || "")}
        />
        <div>
          <label className="block text-xs font-semibold">Servicio</label>
          <select
            name="service_id"
            value={form.service_id || ""}
            onChange={handleChange}
            className="border rounded px-2 py-1 text-sm w-full"
          >
            <option value="">Sin servicio</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {serviceOptionLabel(s)}
              </option>
            ))}
          </select>
          <div className="appointment-field-hint">
            El precio sale del maestro Citas &gt; Servicios.
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold">Médico / recurso</label>
          <select
            name="resource_id"
            value={form.resource_id || ""}
            onChange={handleChange}
            className="border rounded px-2 py-1 text-sm w-full"
          >
            <option value="">Sin médico asignado</option>
            {resources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold">Sede / local</label>
          <select
            name="location_id"
            value={form.location_id || ""}
            onChange={handleChange}
            className="border rounded px-2 py-1 text-sm w-full"
          >
            <option value="">Sin sede</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold">Motivo</label>
          <select
            name="reason_id"
            value={form.reason_id || ""}
            onChange={handleChange}
            className="border rounded px-2 py-1 text-sm w-full"
          >
            <option value="">Sin motivo</option>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold">Inicio *</label>
          <input
            type="datetime-local"
            name="start_at"
            value={form.start_at}
            onChange={handleChange}
            min={!initialData?.id ? todayStartInput(effectiveTimezone) : undefined}
            required
            className="border rounded px-2 py-1 text-sm w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold">
            Fin (opcional: se calcula por servicio)
          </label>
          <input
            type="datetime-local"
            name="end_at"
            value={form.end_at || ""}
            onChange={handleChange}
            min={form.start_at || (!initialData?.id ? todayStartInput(effectiveTimezone) : undefined)}
            className="border rounded px-2 py-1 text-sm w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold">Estado</label>
          <select
            name="status"
            value={form.status || "pending_approval"}
            onChange={handleChange}
            className="border rounded px-2 py-1 text-sm w-full"
          >
            <option value="pending_approval">Pendiente aprobación</option>
            <option value="confirmed">Confirmada</option>
            <option value="canceled">Cancelada</option>
            <option value="completed">Completada</option>
          </select>
        </div>
        <div className="flex items-center gap-2 mt-5">
          <input
            id="requires_approval"
            type="checkbox"
            name="requires_approval"
            checked={!!form.requires_approval}
            onChange={handleChange}
          />
          <label
            htmlFor="requires_approval"
            className="text-xs font-semibold"
          >
            Requiere aprobación
          </label>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold">Notas</label>
        <textarea
          name="notes"
          value={form.notes || ""}
          onChange={handleChange}
          className="border rounded px-2 py-1 text-sm w-full"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-semibold">
            Monto total (USD)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            name="total_amount"
            value={form.total_amount || ""}
            readOnly
            className="border rounded px-2 py-1 text-sm w-full opacity-80 cursor-not-allowed"
          />
          <div className="appointment-field-hint">
            Se llena automáticamente al elegir servicio. Para cambiarlo, edita el precio en Citas &gt; Servicios.
          </div>
        </div>
      </div>

      <div className="pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 rounded text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {submitting ? "Guardando..." : "Guardar cita"}
        </button>
      </div>
    </form>
  );
}
